import { createPredicate } from './condition.js';
import { BrandclubEventType } from './types.js';

// Standard Facebook Pixel event names
const STANDARD_EVENTS = new Set([
    'AddPaymentInfo',
    'AddToCart',
    'AddToWishlist',
    'CompleteRegistration',
    'Contact',
    'CustomizeProduct',
    'Donate',
    'FindLocation',
    'InitiateCheckout',
    'Lead',
    'PageView',
    'Purchase',
    'Schedule',
    'Search',
    'StartTrial',
    'SubmitApplication',
    'Subscribe',
    'ViewContent',
]);
//
let initialized = false;
let debug = false;
const PROVIDER = 'Meta';
let emit = true;
let identity = '';
const randomId = 'id' + Math.random().toString(36).substring(2, 15);
/**
 * Utilities
 */
const warn = (...args) => {
    if (!debug) {
        return;
    }
    // eslint-disable-next-line no-console
    console.info(...['[meta-pixel]'].concat(args));
};
const log = (...args) => {
    if (!debug) {
        return;
    }
    // eslint-disable-next-line no-console
    console.info(...['[meta-pixel]'].concat(args));
};
// function getHourId() {
//   const now = new Date();
//   now.setMinutes(0, 0, 0);
//   const hourTimestamp = now.getTime();
//   return hourTimestamp;
// }
const verifyInit = () => {
    if (!initialized) {
        warn('Pixel not initialized before using call ReactPixel.init with required params');
    }
    return initialized;
};
//
const defaultOptions = {
    autoConfig: true,
};
let activeBrandId = '';
const brandPixels = {};
function isPixelInitialized(brandId, pixelId) {
    var _a;
    const pixels = brandPixels === null || brandPixels === void 0 ? void 0 : brandPixels[brandId];
    if ((_a = pixels === null || pixels === void 0 ? void 0 : pixels.find((item) => item.pixelId === pixelId)) === null || _a === void 0 ? void 0 : _a.initialized) {
        return true;
    }
    return false;
}
function setPixelInitialized(brandId, pixelId) {
    brandPixels[brandId] = brandPixels[brandId].map((item) => {
        if (item.pixelId === pixelId) {
            return Object.assign(Object.assign({}, item), { // keep other properties
                pixelId, initialized: true, eventFilterFn: item.eventFilter ? createPredicate(item.eventFilter) : () => true });
        }
        return item;
    });
}
/**
 * Transforms event payload to Meta Pixel format
 * Maps Brandclub event data to Meta's expected format for ecommerce events
 * Handles: AddToCart, OrderSubmitted, ViewCart, InitiateCheckout, OrderConfirmation
 */
function transformPayloadForMeta(eventType, eventPayload) {
    const transformed = {};
    // Set currency (default to USD)
    transformed.currency = eventPayload.currency || 'USD';
    const items = eventPayload.checkoutItems || eventPayload.cartItems;
    // For events with checkoutItems (cart with multiple items)
    if (items && Array.isArray(items) && items.length > 0) {
        // Extract all product IDs
        const contentIds = items
            .map((item) => item.retailerSku || item.stacklineSku)
            .filter(Boolean);
        if (contentIds.length > 0) {
            transformed.content_ids = contentIds;
        }
        // Use first item's details for content_name and content_category
        const firstItem = items[0];
        if ((firstItem === null || firstItem === void 0 ? void 0 : firstItem.productName) || (firstItem === null || firstItem === void 0 ? void 0 : firstItem.title)) {
            transformed.content_name = firstItem.productName || firstItem.title;
        }
        if (firstItem === null || firstItem === void 0 ? void 0 : firstItem.categoryName) {
            transformed.content_category = firstItem.categoryName;
        }
        // Sum up prices from items
        const totalValue = items.reduce((sum, item) => sum + ((item === null || item === void 0 ? void 0 : item.retailPrice) || 0), 0);
        if (totalValue > 0) {
            transformed.value = totalValue;
        }
        // Add num_items for purchase and checkout events
        if (eventType === BrandclubEventType.PurchaseConfirmed ||
            eventType === BrandclubEventType.OrderConfirmation ||
            eventType === BrandclubEventType.OrderSubmitted ||
            eventType === BrandclubEventType.InitiateCheckout) {
            transformed.num_items = items.length;
        }
    }
    else {
        // Single item event
        if (eventPayload.stacklineSku || eventPayload.retailerSku) {
            transformed.content_ids = [eventPayload.retailerSku || eventPayload.stacklineSku];
        }
        if (eventPayload.productName || eventPayload.brandName) {
            transformed.content_name = eventPayload.productName || eventPayload.brandName;
        }
        if (eventPayload.categoryName) {
            transformed.content_category = eventPayload.categoryName;
        }
        // Set value for single item
        if (eventPayload.retailPrice) {
            transformed.value = eventPayload.retailPrice;
        }
        else if (eventPayload.price) {
            transformed.value = eventPayload.price;
        }
    }
    // Add content_type as product only if we have product identifiers
    if (transformed.content_ids && transformed.content_ids.length > 0) {
        transformed.content_type = 'product';
    }
    return transformed;
}
const metaPixelHelper = {
    setActiveBrand(brand) {
        activeBrandId = brand;
    },
    unload() {
        const scriptElement = document.getElementById(randomId);
        if (scriptElement) {
            log('unloading fbq script ...', document.removeChild(scriptElement) ? 'success' : 'failed');
            window.fbq = undefined;
        }
    },
    setBrandPixel(brand, pixelId, eventFilter) {
        if (brand) {
            brandPixels[brand] = brandPixels[brand] || [];
            if (!brandPixels[brand].find((item) => item.pixelId === pixelId) && pixelId) {
                brandPixels[brand].push({
                    pixelId,
                    eventFilter,
                    initialized: false,
                });
            }
            else {
                // replace
                brandPixels[brand] = brandPixels[brand].map((item) => {
                    if (item.pixelId === pixelId) {
                        return Object.assign(Object.assign({}, item), { // keep other properties
                            pixelId,
                            eventFilter });
                    }
                    return item;
                });
            }
        }
    },
    getActivePixels() {
        return activeBrandId ? brandPixels[activeBrandId] : [];
    },
    initializePixel(brandId, pixelId, options) {
        if (isPixelInitialized(brandId, pixelId)) {
            return;
        }
        metaPixelHelper.init(pixelId, identity ? { external_id: identity } : {}, options || defaultOptions);
        setPixelInitialized(brandId, pixelId);
    },
    init(pixelId, advancedMatching = {}, options = defaultOptions) {
        initialized = typeof window !== 'undefined' && !!window.fbq;
        if (!initialized) {
            initialized = true;
            log('initializing fbq script');
            (function (f, b, e, v, n, t, s) {
                var _a;
                if (f.fbq)
                    return;
                n = f.fbq = function (...args) {
                    n.callMethod ? n.callMethod(...args) : n.queue.push(args);
                };
                if (!f._fbq)
                    f._fbq = n;
                n.push = n;
                n.loaded = !0;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = !0;
                t.id = randomId;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                (_a = s === null || s === void 0 ? void 0 : s.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(t, s);
            })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        }
        if (!pixelId) {
            warn('Please insert pixel id for initializing');
        }
        else {
            if ((options === null || options === void 0 ? void 0 : options.autoConfig) === false) {
                /*
                  The Meta Pixel will send button click and page metadata (such as data structured according to Opengraph or Schema.org formats)
                  from your website to improve your ads delivery and measurement and automate your Pixel setup. To configure the Meta Pixel to not send this additional
                  information, in the Meta Pixel Base code, add fbq('set', 'autoConfig', 'false', 'FB_PIXEL_ID') above the init call.
                */
                window.fbq('set', 'autoConfig', false, pixelId);
                log(`called fbq('set', 'autoConfig', false, '${pixelId}');`);
            }
            window.fbq('init', pixelId, advancedMatching);
            log(`called fbq('init', '${pixelId}');`);
        }
    },
    trackSingle(title, data, isCustom = false) {
        if (!verifyInit()) {
            return;
        }
        const activePixels = metaPixelHelper.getActivePixels();
        const eventType = isCustom ? 'trackSingleCustom' : 'trackSingle';
        if (activePixels && (activePixels === null || activePixels === void 0 ? void 0 : activePixels.length) > 0) {
            activePixels.forEach((pixel) => {
                if (emit) {
                    if (pixel.eventFilterFn && !pixel.eventFilterFn(data)) {
                        log('eventFilterFn failed', pixel.eventFilterFn, data);
                        return;
                    }
                    window.fbq(eventType, pixel.pixelId, title, data);
                }
                if (debug) {
                    log(`called fbq('${eventType}', '${pixel.pixelId}', '${title}'); with data `, data);
                }
            });
        }
    },
    trackSingleCustom(eventName, data) {
        return metaPixelHelper.trackSingle(eventName, data, true);
    },
};
/**
 * Set Meta's Limited Data Use (LDU) data processing options
 * Must be called BEFORE the track event for the affected conversion
 *
 * @param lduOptions - LDU configuration with country and state
 * @see https://developers.facebook.com/docs/meta-pixel/implementation/data-processing-options
 */
function setDataProcessingOptions(lduOptions) {
    if (!verifyInit()) {
        return;
    }
    if (lduOptions.enabled) {
        // Enable LDU: fbq('dataProcessingOptions', ['LDU'], country, state)
        // country: 1 = US, state: 0 = auto-detect
        window.fbq('dataProcessingOptions', ['LDU'], lduOptions.country, lduOptions.state);
        log(`LDU enabled: dataProcessingOptions(['LDU'], ${lduOptions.country}, ${lduOptions.state})`);
    }
    else {
        // Clear LDU restrictions
        window.fbq('dataProcessingOptions', [], 0, 0);
        log('LDU disabled: dataProcessingOptions([], 0, 0)');
    }
}
const metaPixel = {
    enableDebug() {
        debug = true;
    },
    setIdentity(id) {
        identity = id;
    },
    enableEmitMetrics(v = true) {
        emit = v;
    },
    /**
     * Set LDU data processing options (exposed for external use if needed)
     */
    setDataProcessingOptions,
    track({ brandId, pixels, eventName, eventType, eventPayload, trackingOptions, }) {
        var _a;
        pixels = pixels === null || pixels === void 0 ? void 0 : pixels.filter((item) => item.pixelProvider === PROVIDER);
        metaPixelHelper.setActiveBrand(brandId);
        if (Array.isArray(pixels) && (pixels === null || pixels === void 0 ? void 0 : pixels.length) > 0) {
            // log(JSON.stringify({ brandId, pixels, eventName, payload: eventPayload }));
            if (pixels.length > 0) {
                pixels.forEach((pixel) => {
                    metaPixelHelper.setBrandPixel(brandId, pixel.pixelId, pixel.eventFilter);
                    metaPixelHelper.initializePixel(brandId, pixel.pixelId, pixel.pixelOptions);
                });
            }
            // Apply LDU for purchase/conversion events if user is in an affected state
            // Must be called BEFORE the track event
            const isPurchaseEvent = eventType === BrandclubEventType.PurchaseConfirmed ||
                eventType === BrandclubEventType.OrderSubmitted ||
                eventType === BrandclubEventType.OrderConfirmation;
            if (isPurchaseEvent && ((_a = trackingOptions === null || trackingOptions === void 0 ? void 0 : trackingOptions.ldu) === null || _a === void 0 ? void 0 : _a.enabled)) {
                setDataProcessingOptions(trackingOptions.ldu);
            }
            // Transform payload for all ecommerce events
            if (eventType === BrandclubEventType.AddToCart ||
                eventType === BrandclubEventType.PurchaseConfirmed ||
                eventType === BrandclubEventType.OrderSubmitted ||
                eventType === BrandclubEventType.ViewCart ||
                eventType === BrandclubEventType.InitiateCheckout ||
                eventType === BrandclubEventType.OrderConfirmation) {
                eventPayload = transformPayloadForMeta(eventType, eventPayload);
            }
            // Auto-detect if event is standard or custom based on event name
            const isCustomEvent = !STANDARD_EVENTS.has(eventName);
            metaPixelHelper.trackSingle(eventName, eventPayload, isCustomEvent);
        }
        else {
            metaPixelHelper.unload();
        }
    },
    grantConsent() {
        if (!verifyInit()) {
            return;
        }
        window.fbq('consent', 'grant');
        log(`Meta Pixel consent granted`);
    },
    revokeConsent() {
        if (!verifyInit()) {
            return;
        }
        window.fbq('consent', 'revoke');
        log(`Meta Pixel consent revoked`);
    },
};

export { metaPixel as default };
//# sourceMappingURL=metaPixel.js.map
